import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Skeleton } from '@mui/material';
import { useTranslate } from 'react-admin';
import marketplaceService, {
    extractErrorMessage,
    ListingDetailResult,
} from '../services/marketplaceService';
import tokenService from '../services/tokenService';
import {
    allowedLicenseTypes,
    BuyerLicenseType,
    canLicenseCourses,
    firstAllowedLicenseType,
    formatEuro,
    originKey,
    requiresPayPal,
    resolveSettlement,
    totalPriceCents,
} from './marketplaceVisibility';
import MaterialSymbol from '../components/common/MaterialSymbol';
import { VisibilityBadge } from '../components/Marketplace/Seller/SellerBadges';
import PriceBlock from '../components/Marketplace/Buyer/PriceBlock';
import { DIFFICULTY_KEYS, ListingThumbnail } from '../components/Marketplace/Buyer/ListingCard';
import CheckoutDialog from '../components/Marketplace/Checkout/CheckoutDialog';
import ListingReviews from '../components/Marketplace/Reviews/ListingReviews';
import '../components/Marketplace/marketplace-design.css';

/** Lektionstypen, die in der Inhaltsliste als „Test“ erscheinen (alles andere: „Lektion“). */
const TEST_TYPES = ['multiple_choice', 'fill_in_blank', 'free_text', 'drag_drop', 'matching', 'sorting', 'hotspot'];

type DetailLesson = NonNullable<ListingDetailResult['listing']['course']['lessons']>[number];
const LESSONS_FLAT = 'flat';

type DetailSection = { id: number; title: string };
type LessonGroup = { key: string; title: string | null; lessons: DetailLesson[] };

/** Ein Kurs der Detailseite mit seinen Gruppen (Lektionen ohne Sektion zuerst, danach je Sektion). */
interface CourseBlock {
    id: number;
    title: string;
    lessons: DetailLesson[];
    minutes: number;
    groups: LessonGroup[];
    numbers: Map<number, number>;
}

const buildBlock = (
    course: { id: number; title?: string; sections?: DetailSection[]; lessons?: DetailLesson[] },
    prefix: string,
): CourseBlock => {
    const lessons = course.lessons ?? [];
    const sections = course.sections ?? [];
    const sectionIds = new Set(sections.map((s) => s.id));
    const orphan = lessons.filter((l) => !l.sectionId || !sectionIds.has(l.sectionId));
    const groups: LessonGroup[] = [];
    if (orphan.length > 0) groups.push({ key: `${prefix}${sections.length ? 'orphan' : LESSONS_FLAT}`, title: null, lessons: orphan });
    sections.forEach((s) => {
        const own = lessons.filter((l) => l.sectionId === s.id);
        if (own.length > 0) groups.push({ key: `${prefix}s${s.id}`, title: s.title, lessons: own });
    });
    // Fortlaufende Nummerierung je Kurs über alle Sektionen, auch wenn Sektionen eingeklappt sind
    const numbers = new Map<number, number>();
    groups.forEach((g) => g.lessons.forEach((l) => numbers.set(l.id, numbers.size + 1)));
    return {
        id: course.id,
        title: course.title ?? '',
        lessons,
        minutes: lessons.reduce((sum, l) => sum + (l.estimatedDuration ?? 0), 0),
        groups,
        numbers,
    };
};

const initialsOf = (name: string): string =>
    name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');


/**
 * Detailseite eines Angebots (`/marketplace/listings/:slug`, Plan T6) nach der
 * Design-Vorlage „Angebot-Detail“: Titel mit Pills, Kurzfakten, Highlights,
 * Inhalt (Sektionen/Lektionen mit Typ), Schlagwörter, Anbieter-Karte,
 * Bewertungen und rechts die Kaufbox „Lizenz wählen“ (Sticky, unter 900 px
 * unter dem Inhalt). Die Kaufbox öffnet den bestehenden Checkout-Dialog.
 */
const MarketplaceListingPage: React.FC = () => {
    const { slug = '' } = useParams<{ slug: string }>();
    const translate = useTranslate();
    const canBuy = canLicenseCourses(tokenService.getRoles());

    const [data, setData] = useState<ListingDetailResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [selected, setSelected] = useState<BuyerLicenseType | null>(null);
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    // `silent`: Aktualisierung nach dem Kauf, ohne den offenen Dialog abzubauen
    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setError(null);
        try {
            setData(await marketplaceService.getListingBySlug(slug));
        } catch (err: any) {
            setError(
                err?.response?.status === 404
                    ? translate('marketplaceBuyer.detail.notFound')
                    : extractErrorMessage(err, translate('marketplaceBuyer.loadError')),
            );
        } finally {
            setLoading(false);
        }
    }, [slug, translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [slug]);

    // Ein Angebot kann mehrere Kurse enthalten (Bundle); ältere Antworten ohne `courses` = Einzelkurs.
    const bundleCourses = data?.listing.courses ?? [];
    const isBundle = bundleCourses.length > 1;
    const blocks = useMemo((): CourseBlock[] => {
        if (!data) return [];
        if (isBundle) return bundleCourses.map((c) => buildBlock(c, `c${c.id}-`));
        return [buildBlock(data.listing.course, '')];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isBundle]);
    const lessons: DetailLesson[] = useMemo(() => blocks.flatMap((b) => b.lessons), [blocks]);
    const allGroupKeys = useMemo(() => blocks.flatMap((b) => b.groups.map((g) => g.key)), [blocks]);

    const back = (
        <Link to="/marketplace" className="mk-back">
            <MaterialSymbol name="arrow_back" />
            {translate('marketplaceBuyer.detail.back')}
        </Link>
    );

    if (loading) {
        return (
            <div className="mk-detail-page">
                <Skeleton variant="rectangular" height={160} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={320} />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="mk-detail-page">
                {back}
                <div className="mk-alert err" role="alert">
                    <MaterialSymbol name="error" />
                    <div className="body">{error ?? translate('marketplaceBuyer.detail.notFound')}</div>
                </div>
            </div>
        );
    }

    const { listing, existingLicense } = data;
    const types = allowedLicenseTypes(listing);
    const type: BuyerLicenseType = selected && types.includes(selected) ? selected : firstAllowedLicenseType(listing);
    const own = listing.origin === 'own';
    const licensed = !!existingLicense && ['pending', 'active', 'suspended'].includes(existingLicense.status);
    const checkoutListing = { ...listing, alreadyLicensed: licensed };

    const totalMinutes = blocks.reduce((sum, b) => sum + b.minutes, 0);
    const formatMinutes = (minutes: number): string => translate('marketplaceBuyer.detail.minutes', { count: minutes });
    const durationFact = totalMinutes >= 60
        ? translate('marketplaceBuyer.detail.hours', {
            value: (Math.round((totalMinutes / 60) * 2) / 2).toLocaleString('de-DE', { maximumFractionDigits: 1 }),
        })
        : formatMinutes(totalMinutes);

    // Info-Pill: erstes kurzes Highlight (z. B. „BaFin-anerkannt“), falls vorhanden
    const infoPill = (listing.highlights ?? []).find((h) => h.length > 0 && h.length <= 28);
    const tags = listing.tags ?? [];
    const description = listing.description;
    const courseDescription = listing.course.description;

    const courseKeys = isBundle ? blocks.map((b) => `course-${b.id}`) : [];
    const collapsibleKeys = [...courseKeys, ...allGroupKeys];
    const allCollapsed = collapsibleKeys.length > 0 && collapsibleKeys.every((k) => collapsed.has(k));
    const toggleAll = () => setCollapsed(allCollapsed ? new Set() : new Set(collapsibleKeys));
    const toggleGroup = (key: string) =>
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const settlement = resolveSettlement(listing, type, 1);
    const sumCents = totalPriceCents(listing, type, 1);
    const sellerName = listing.seller?.name || translate('marketplaceBuyer.detail.sellerFallback');
    const sellerCount = listing.seller?.listingCount ?? 0;


    return (
        <div className="mk-detail-page">
            {back}
            <div className="mk-dgrid">
                <div className="mk-dmain">
                    <div className="mk-card mk-dsec">
                        <ListingThumbnail src={listing.thumbnailUrl} height={220} />
                        <div className="mk-dhead">
                            <VisibilityBadge visibility={originKey(listing)} />
                            {isBundle && (
                                <span className="mk-pill neu" data-testid="detail-bundle-pill">
                                    <MaterialSymbol name="layers" />
                                    {translate('marketplaceBuyer.bundle.pill', { count: bundleCourses.length })}
                                </span>
                            )}
                            {listing.course.difficulty && (
                                <span className="mk-pill neu">
                                    {translate(DIFFICULTY_KEYS[listing.course.difficulty] ?? listing.course.difficulty, { _: listing.course.difficulty })}
                                </span>
                            )}
                            {listing.category && <span className="mk-pill neu">{listing.category.name}</span>}
                            <span className="sp" />
                            {infoPill && (
                                <span className="mk-pill info" data-testid="detail-info-pill">
                                    <MaterialSymbol name="verified" />
                                    {infoPill}
                                </span>
                            )}
                        </div>
                        <h1 className="mk-dtitle">{listing.title}</h1>
                        {description && <p className="mk-dlead">{description}</p>}
                        {!isBundle && courseDescription && courseDescription !== description && <p className="mk-dsub">{courseDescription}</p>}

                        <div className="mk-dfacts">
                            {isBundle && (
                                <div className="mk-dfact" data-testid="detail-fact-courses">
                                    <MaterialSymbol name="layers" />
                                    <div><b>{translate('marketplaceBuyer.bundle.factCourses', { count: bundleCourses.length })}</b></div>
                                </div>
                            )}
                            {lessons.length > 0 && (
                                <div className="mk-dfact">
                                    <MaterialSymbol name="menu_book" />
                                    <div><b>{translate('marketplaceBuyer.detail.factLessons', { count: lessons.length })}</b></div>
                                </div>
                            )}
                            {totalMinutes > 0 && (
                                <div className="mk-dfact">
                                    <MaterialSymbol name="schedule" />
                                    <div><b>{durationFact}</b>{translate('marketplaceBuyer.detail.factDuration')}</div>
                                </div>
                            )}
                        </div>

                        {listing.previewVideoUrl && (
                            <a
                                className="mk-btn-o mk-sm mk-dpreview"
                                href={listing.previewVideoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <MaterialSymbol name="open_in_new" />
                                {translate('marketplaceBuyer.detail.previewVideo')}
                            </a>
                        )}

                        {!!listing.highlights?.length && (
                            <section aria-labelledby="highlights-heading">
                                <h2 id="highlights-heading">{translate('marketplaceBuyer.detail.highlights')}</h2>
                                <ul className="mk-hl">
                                    {listing.highlights.map((h, i) => (
                                        <li key={i}><MaterialSymbol name="check_circle" />{h}</li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {allGroupKeys.length > 0 && (
                            <section aria-labelledby="content-heading" data-testid="detail-content">
                                <div className="mk-dh2row">
                                    <h2 id="content-heading">{translate('marketplaceBuyer.detail.content')}</h2>
                                    <button type="button" className="mk-btn-text" onClick={toggleAll} data-testid="detail-toggle-all">
                                        {translate(allCollapsed ? 'marketplaceBuyer.detail.expandAll' : 'marketplaceBuyer.detail.collapseAll')}
                                    </button>
                                </div>
                                {blocks.map((block) => {
                                    const courseKey = `course-${block.id}`;
                                    const courseCollapsed = isBundle && collapsed.has(courseKey);
                                    return (
                                        <div className={isBundle ? 'mk-cblock' : undefined} key={block.id} data-testid={isBundle ? `detail-course-${block.id}` : undefined}>
                                            {isBundle && (
                                                <button
                                                    type="button"
                                                    className="mk-cblock-h"
                                                    aria-expanded={!courseCollapsed}
                                                    aria-label={translate('marketplaceBuyer.detail.toggleCourse', { title: block.title })}
                                                    onClick={() => toggleGroup(courseKey)}
                                                >
                                                    <MaterialSymbol name="expand_more" className={courseCollapsed ? 'mk-rot' : undefined} />
                                                    <span className="t">{block.title}</span>
                                                    <span className="n">
                                                        {translate('marketplaceBuyer.detail.factLessons', { count: block.lessons.length })}
                                                        {block.minutes > 0 && ` · ${formatMinutes(block.minutes)}`}
                                                    </span>
                                                </button>
                                            )}
                                            {!courseCollapsed && block.groups.map((group) => {
                                                const isCollapsed = collapsed.has(group.key);
                                                return (
                                                    <div className="mk-lgroup" key={group.key} data-testid={`detail-group-${group.key}`}>
                                                        {group.title && (
                                                            <button
                                                                type="button"
                                                                className="mk-lgroup-h"
                                                                aria-expanded={!isCollapsed}
                                                                aria-label={translate('marketplaceBuyer.detail.toggleSection', { title: group.title })}
                                                                onClick={() => toggleGroup(group.key)}
                                                            >
                                                                <MaterialSymbol name="expand_more" className={isCollapsed ? 'mk-rot' : undefined} />
                                                                <span className="t">{group.title}</span>
                                                                <span className="n">{translate('marketplaceBuyer.detail.factLessons', { count: group.lessons.length })}</span>
                                                            </button>
                                                        )}
                                                        {!isCollapsed && (
                                                            <ol className="mk-lessons">
                                                                {group.lessons.map((lesson) => {
                                                                    const isTest = TEST_TYPES.includes(lesson.type ?? '');
                                                                    return (
                                                                        <li key={lesson.id}>
                                                                            <span className="num">{block.numbers.get(lesson.id)}</span>
                                                                            <span className="nm">{lesson.title}</span>
                                                                            <span className={`mk-pill ${isTest ? 'warn' : 'neu'} kind`}>
                                                                                {translate(`marketplaceBuyer.detail.kind.${isTest ? 'test' : 'lesson'}`)}
                                                                            </span>
                                                                            {!!lesson.estimatedDuration && (
                                                                                <span className="dur">
                                                                                    <MaterialSymbol name="schedule" />
                                                                                    {formatMinutes(lesson.estimatedDuration)}
                                                                                </span>
                                                                            )}
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ol>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    );
                                })}
                            </section>
                        )}

                        {tags.length > 0 && (
                            <section aria-labelledby="tags-heading">
                                <h2 id="tags-heading">{translate('marketplaceBuyer.detail.tags')}</h2>
                                <div className="mk-tags">
                                    {tags.map((tag) => <span className="mk-pill line" key={tag}>{tag}</span>)}
                                </div>
                            </section>
                        )}
                    </div>

                    <div className="mk-card mk-gap-top" data-testid="detail-seller">
                        <div className="mk-seller">
                            <span className="av">{listing.seller?.name ? initialsOf(listing.seller.name) : <MaterialSymbol name="storefront" />}</span>
                            <div className="tx">
                                <b>{sellerName}</b>
                                <span>
                                    {translate('marketplaceBuyer.detail.sellerInfo', { smart_count: sellerCount })}
                                    {listing.origin === 'public' && ` · ${translate('marketplaceBuyer.detail.sellerInvoice')}`}
                                </span>
                            </div>
                            <Link to="/marketplace" className="mk-lnk">
                                {translate('marketplaceBuyer.detail.allOffers')}
                                <MaterialSymbol name="arrow_forward" />
                            </Link>
                        </div>
                    </div>

                    {/* T9: Bewertungen; der Anbieter des eigenen Angebots kann antworten */}
                    <div className="mk-gap-top">
                        <ListingReviews
                            listingId={listing.id}
                            canReply={own && tokenService.getRoles().some((r) => ['ROLE_CONTENT_MANAGER', 'ROLE_COMPANY_ADMIN', 'ROLE_PLATFORM_ADMIN', 'ROLE_OPERATOR'].includes(r))}
                        />
                    </div>
                </div>

                <aside className="mk-card mk-buy" aria-labelledby="buy-heading" data-testid="detail-buybox">
                    <h3 id="buy-heading">{translate('marketplaceBuyer.detail.chooseLicense')}</h3>
                    <div role="radiogroup" aria-label={translate('marketplaceBuyer.detail.licenseOptions')}>
                        {types.map((t) => (
                            <label className={`mk-lic${t === type ? ' on' : ''}`} key={t} data-testid={`detail-license-${t}`}>
                                <input
                                    type="radio"
                                    name="license-type"
                                    value={t}
                                    checked={t === type}
                                    onChange={() => setSelected(t)}
                                />
                                <span className="t">
                                    <span className="k">
                                        <b>{translate(`marketplaceBuyer.licenseType.${t}.label`)}</b>
                                        <span className="p"><PriceBlock listing={listing} type={t} variant="large" /></span>
                                    </span>
                                    <p>{translate(`marketplaceBuyer.licenseType.${t}.hint`)}</p>
                                </span>
                            </label>
                        ))}
                    </div>
                    <div className="mk-sum" data-testid="detail-sum">
                        <span>{translate('marketplaceBuyer.detail.sum')}</span>
                        <b>{sumCents === 0 ? translate('marketplaceBuyer.price.free') : formatEuro(sumCents / 100, listing.currency)}</b>
                    </div>
                    {isBundle && (
                        <p className="mk-vat" data-testid="detail-bundle-price-note">
                            {translate('marketplaceBuyer.bundle.priceNote', { count: bundleCourses.length })}
                        </p>
                    )}
                    <p className="mk-vat">
                        {requiresPayPal(settlement)
                            ? translate('marketplaceBuyer.detail.vatNote')
                            : translate(`marketplaceBuyer.checkout.settlement.${settlement}`)}
                    </p>

                    {own && (
                        <div className="mk-alert info">
                            <MaterialSymbol name="info" />
                            <div className="body">{translate('marketplaceBuyer.detail.own')}</div>
                        </div>
                    )}
                    {licensed && (
                        <>
                            <button type="button" className="mk-btn-s mk-done mk-full" disabled>
                                <MaterialSymbol name="check" />
                                {translate('marketplaceBuyer.card.licensed')}
                            </button>
                            <p className="mk-vat mk-vat-link">
                                {translate(isBundle ? 'marketplaceBuyer.bundle.alreadyLicensed' : 'marketplaceBuyer.checkout.alreadyLicensed')}{' '}
                                <Link to="/marketplace/licenses" className="mk-lnk">{translate('marketplaceBuyer.nav.licenses')}</Link>
                            </p>
                        </>
                    )}
                    {!own && !licensed && canBuy && (
                        <button type="button" className="mk-btn-s mk-full" onClick={() => setCheckoutOpen(true)}>
                            <MaterialSymbol name="shopping_cart" />
                            {translate('marketplaceBuyer.card.license')}
                        </button>
                    )}
                    {!own && !licensed && !canBuy && (
                        <div className="mk-alert info">
                            <MaterialSymbol name="info" />
                            <div className="body">{translate('marketplaceBuyer.readOnlyHint')}</div>
                        </div>
                    )}
                    {requiresPayPal(settlement) && !own && !licensed && (
                        <ul className="mk-trust">
                            <li><MaterialSymbol name="check" />{translate('marketplaceBuyer.detail.trustInstant')}</li>
                            <li><MaterialSymbol name="check" />{translate('marketplaceBuyer.detail.trustInvoice')}</li>
                        </ul>
                    )}
                </aside>
            </div>

            <CheckoutDialog
                open={checkoutOpen}
                listing={checkoutListing}
                initialType={type}
                onClose={() => setCheckoutOpen(false)}
                onCompleted={() => load(true)}
            />
        </div>
    );
};

export default MarketplaceListingPage;
